import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { placementDriveService, vacancyService } from '../services/api';
import './DriveDetails.css';

function DriveDetails() {
  const { id } = useParams();
  const [drive, setDrive] = useState(null);
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDriveDetails();
  }, [id]);

  const loadDriveDetails = async () => {
    try {
      setLoading(true);
      // Fetch all placement drives and find the one with matching ID
      const [drivesData, vacanciesData] = await Promise.all([
        placementDriveService.GetNewPlacementDrives(),
        vacancyService.getVacanciesByPlacement(id)
      ]);
      
      const foundDrive = drivesData.find(d => d.Id === parseInt(id));
      if (foundDrive) {
        setDrive(foundDrive);
      }
      setVacancies(vacanciesData);
    } catch (error) {
      console.error('Error loading drive details:', error);
      alert('Error loading drive details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (lastDate) => {
    if (!lastDate) return 'Upcoming';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDateObj = new Date(lastDate);
    lastDateObj.setHours(0, 0, 0, 0);
    
    if (lastDateObj < today) {
      return 'Completed';
    } else if (lastDateObj.getTime() === today.getTime()) {
      return 'Ongoing';
    } else {
      return 'Upcoming';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getLocation = (drive) => {
    if (!drive) return 'Location not specified';
    const parts = [];
    if (drive.College?.Name) parts.push(drive.College.Name);
    if (drive.District?.Name) parts.push(drive.District.Name);
    if (drive.State?.Name) parts.push(drive.State.Name);
    if (drive.Country?.Name) parts.push(drive.Country.Name);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  if (loading) {
    return (
      <div className="drive-details">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading drive details...</p>
        </div>
      </div>
    );
  }

  if (!drive) {
    return (
      <div className="drive-details">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Drive not found.</p>
          <Link to="/placement-drives" className="back-link">← Back to Drives</Link>
        </div>
      </div>
    );
  }

  const status = getStatus(drive.LastDate);

  return (
    <div className="drive-details">
      <div className="drive-details-header">
        <div className="container">
          <Link to="/placement-drives" className="back-link">← Back to Drives</Link>
          <div className="drive-header-content">
            <div className="drive-header-image">
              <div className="drive-header-icon">🏢</div>
            </div>
            <div className="drive-header-info">
              <h1>{drive.Title}</h1>
              <p className="drive-company-name">{drive.Company?.Name || 'Company not specified'}</p>
              <div className="drive-meta">
                <span>📍 {getLocation(drive)}</span>
                <span>📅 {formatDate(drive.Date)}</span>
                {drive.LastDate && <span>⏰ Last Date: {formatDate(drive.LastDate)}</span>}
                <span className={`status-badge ${status.toLowerCase()}`}>
                  {status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {drive.Description && (
          <div className="drive-description-section">
            <h2>About This Drive</h2>
            <p>{drive.Description}</p>
          </div>
        )}

        <div className="vacancies-section">
          <h2>Available Vacancies ({vacancies.length})</h2>
          <p className="section-subtitle">Click on any vacancy to view details and apply</p>
          
          {vacancies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '10px' }}>
              <p>No vacancies available for this placement drive.</p>
            </div>
          ) : (
            <div className="vacancies-grid">
              {vacancies.map(vacancy => (
                <Link 
                  key={vacancy.Id} 
                  to={`/vacancy/${vacancy.Id}?driveId=${drive.Id}`}
                  className="vacancy-card-link"
                >
                  <div className="vacancy-card">
                    <div className="vacancy-header">
                      <h3>{vacancy.Title}</h3>
                      {vacancy.LastDate && (
                        <span className="vacancy-salary">Due: {formatDate(vacancy.LastDate)}</span>
                      )}
                    </div>
                    <div className="vacancy-details">
                      {vacancy.Company?.Name && (
                        <p><strong>Company:</strong> {vacancy.Company.Name}</p>
                      )}
                      {vacancy.Description && (
                        <p className="vacancy-requirements">
                          <strong>Description:</strong> {
                            vacancy.Description.length > 100 
                              ? vacancy.Description.substring(0, 100) + '...' 
                              : vacancy.Description
                          }
                        </p>
                      )}
                      {vacancy.Conditions && (
                        <p className="vacancy-requirements">
                          <strong>Conditions:</strong> {
                            vacancy.Conditions.length > 100 
                              ? vacancy.Conditions.substring(0, 100) + '...' 
                              : vacancy.Conditions
                          }
                        </p>
                      )}
                    </div>
                    <button className="apply-now-btn">View Details & Apply →</button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriveDetails;

